import { z } from 'zod';
import { JsonObject, LinkedUser, RecordAccount, Timestamp, Uuid } from './common.js';
import { Analysis } from './analyses.js';
import { DecisionSummary } from './decisions.js';
import { Fact, HeldClaim } from './facts.js';
import { InteractionEntry, NoteEntry, PROSE_NOTE_MAX_LENGTH } from './inputs.js';
import {
  PROFILE_LINK_ACTIONS,
  PROFILE_LINK_PLATFORMS,
  RunKind,
  RunState,
  ProseInputKind,
  SubjectStatus,
} from './vocab.js';
import { RunSummary } from './runs.js';

/* ————————————————————————— create ————————————————————————— */

/**
 * `POST /subjects` — person creation; the intake prose is read on its own leg
 * afterwards, so this answers the moment the rows are written and the note it
 * hands back reads `pending`.
 * `status` is required — every subject has one — and it is the **org's
 * vertical** that says which values it accepts: a value outside the vocabulary
 * fails validation, and one inside it that is not live yet 400s
 * `STATUS_NOT_YET_SUPPORTED` — which no registered vertical says today, both
 * having gone fully live. It is not a mistake at intake either way: the word
 * can be changed afterwards through `POST /subjects/:id/status`.
 * The response is the seeded record: the same `SubjectRecord` shape
 * `GET /subjects/:id/record` returns.
 *
 * **The name collision (decided 2026-09-01).** Research mints satellite person
 * entities by name — a subject's spouse, her brother, the friend who introduced
 * them — so a human being entered as a *new* subject may already be a node in
 * the graph. Entering him again minted a second Andrew East in prod. A bare
 * create therefore 409s `NAME_CONFLICT` when the org holds a live person under
 * the same normalized name, and hands back the namesakes so the office can say
 * which of two things is true. Neither answer is inferable from the name:
 *
 *  - `entity_id` — **the same person.** Adopt that existing entity as the
 *    subject: everything already known about them carries over, and no second
 *    node is minted. 409 `ENTITY_ALREADY_SUBJECT` if they are already a subject.
 *  - `force_new: true` — **a different person who shares the name.** Mint the
 *    new entity unconditionally. Two live namesakes is a legitimate state, which
 *    is why `entity.normalized_name` carries no unique index.
 *
 * The two are mutually exclusive, and neither is needed when nothing collides.
 *
 * **`prose` is required to mint and optional to adopt** (ruled 2026-09-06). A
 * person entered from nothing is entered with a sentence about why — it is the
 * only thing on the record at that moment, and the intake extraction over it is
 * what seeds the anchors. An adopt is the opposite case: everything known about
 * this person is already on the entity, which is the whole point of adopting it,
 * and the office pressed Track on a row in a list rather than writing anything.
 *
 * The People screen's adopt door used to satisfy the requirement with a
 * fabricated line — "Tracked from the people list." — which cost an Opus-class
 * extraction over a sentence containing no fact, and stored a sentence nobody
 * had written as the office's own words. Both are worse than the field being
 * absent. So it may be absent, and only when `entity_id` says there is a record
 * already there to seed from.
 */
export const CreateSubjectBody = z
  .object({
    name: z.string().min(1),
    status: SubjectStatus,
    /** Why this person is being entered. Required unless {@link entity_id} is given. */
    /* Trimmed and bounded like the add box's, and for the same reason: this
       body reaches the splitter too, so its length is a count of reading legs
       (`PROSE_NOTE_MAX_LENGTH`, `shared/src/inputs.ts`). */
    prose: z.string().trim().min(1).max(PROSE_NOTE_MAX_LENGTH).optional(),
    /** Adopt this live person entity as the subject instead of minting one. */
    entity_id: Uuid.optional(),
    /** Mint a new person even though a live namesake exists. */
    force_new: z.literal(true).optional(),
    /**
     * **The protection account this person belongs to** — required in a security
     * org, refused in every other, and never defaulted.
     *
     * Optional here rather than required-and-nullable because whether it applies
     * at all is the *org's vertical's* answer, which this package deliberately
     * does not model (§0.7): a club has no account to name, and a body that owed
     * an explicit `null` would make every existing caller wrong. The server, which
     * knows the vertical, refuses an absent one in a security org and a present
     * one anywhere else.
     */
    account_id: Uuid.optional(),
  })
  .strict()
  .refine((body) => !(body.entity_id !== undefined && body.force_new), {
    message: 'entity_id and force_new are mutually exclusive',
    path: ['force_new'],
  })
  .refine((body) => body.prose !== undefined || body.entity_id !== undefined, {
    message: 'prose is required unless entity_id is given',
    path: ['prose'],
  });
export type CreateSubjectBody = z.infer<typeof CreateSubjectBody>;

/**
 * One live namesake, as `NAME_CONFLICT` reports it. Everything here is what the
 * office needs to tell two people apart *without* leaving the form — how long
 * the record has stood, how much is on it, and how it is tied to people they
 * already know. No score and no "likelihood": the collision is exact-name and
 * the judgment is the reader's.
 */
export const NameConflictCandidate = z.object({
  entity_id: Uuid,
  display_name: z.string(),
  created_at: Timestamp,
  /** Whether this person already has a subject record of their own. */
  is_subject: z.boolean(),
  /** Active facts where they are the subject or the counterparty. */
  fact_count: z.number().int().nonnegative(),
  /**
   * Up to three human-readable pair-fact snippets — "Spouse of Shawn Johnson".
   * Empty when the entity has no pair fact that renders honestly; a junk line is
   * worse than no line.
   */
  relationships: z.array(z.string()),
});
export type NameConflictCandidate = z.infer<typeof NameConflictCandidate>;

/** `NAME_CONFLICT` details — the name as entered, and the live namesakes. */
export const NameConflictDetails = z.object({
  name: z.string(),
  candidates: z.array(NameConflictCandidate),
});
export type NameConflictDetails = z.infer<typeof NameConflictDetails>;

/** `ENTITY_ALREADY_SUBJECT` details — the record the adopt would have duplicated. */
export const EntityAlreadySubjectDetails = z.object({
  entity_id: Uuid,
  display_name: z.string(),
});
export type EntityAlreadySubjectDetails = z.infer<typeof EntityAlreadySubjectDetails>;

/* ————————————————————————— list ————————————————————————— */

/**
 * How the People list is ordered. `updated` is what it has always been — the
 * most recently touched person first; `name` is the alphabetical read a roster
 * of 2,650 people needs (import note §1.18).
 *
 * Exported as a const array so the screen builds its control from the
 * vocabulary rather than restating it.
 */
export const SUBJECT_LIST_SORTS = ['updated', 'name'] as const;
export const SubjectListSort = z.enum(SUBJECT_LIST_SORTS);
export type SubjectListSort = z.infer<typeof SubjectListSort>;

/**
 * **What a person is to the org** (import note §1.3), and the two levels a list
 * entry can carry:
 *
 * - `tracked` — a subject. A `subject_record` row, a status from the org's
 *   vertical, researchable, everything the People list has ever listed.
 * - `known` — a person entity of this org with no subject record: the spouse
 *   research minted, and every person an import kept without promoting. They
 *   are the resolver's vocabulary and they are findable, which is what this
 *   level is for; they have no status, no runs and no identity of their own.
 *
 * The third value below is the *filter's* and not an entry's: nothing is both.
 */
export const SUBJECT_LEVELS = ['tracked', 'known'] as const;
export const SubjectLevel = z.enum(SUBJECT_LEVELS);
export type SubjectLevel = z.infer<typeof SubjectLevel>;

/** The `level` filter's values — the two levels, plus `all` for both at once. */
export const SUBJECT_LIST_LEVELS = [...SUBJECT_LEVELS, 'all'] as const;
export const SubjectListLevel = z.enum(SUBJECT_LIST_LEVELS);
export type SubjectListLevel = z.infer<typeof SubjectListLevel>;

/** Completed research depth; full research takes precedence over quick search. */
export const SUBJECT_RESEARCH_STATUSES = ['none', 'quick_search', 'research'] as const;
export const SubjectResearchStatus = z.enum(SUBJECT_RESEARCH_STATUSES);
export type SubjectResearchStatus = z.infer<typeof SubjectResearchStatus>;

/**
 * A repeatable query parameter, as one arrives on the wire: `?status=member`
 * gives a bare string and `?status=member&status=contact` gives an array, and
 * the schema must not care which the caller sent.
 */
const repeatable = (value: unknown): unknown =>
  value === undefined || Array.isArray(value) ? value : [value];

/**
 * `GET /subjects` query. `limit` defaults to 50, caps at 200.
 *
 * `status` is **repeatable**, allowing API callers to select several types,
 * and it is deliberately typed as bare strings here rather than as
 * `SubjectStatus`: which words exist is the *org's vertical's* to say (§7.9a),
 * so the server vets them against that vocabulary and answers 400
 * `VALIDATION_FAILED` for a word this org does not have. A shared enum would
 * have accepted another vertical's words and refused none of them usefully.
 *
 * `level` defaults to `tracked`, which is the list as it stood: known people
 * are opt-in, so no existing caller's page grows people it never asked for.
 * A `status` filter and `level: 'known'` select nothing between them — a known
 * person has no status — and that is the honest answer rather than an error.
 */
/** How far `near` reaches around a city when no radius is given, and at most. */
export const DEFAULT_NEAR_RADIUS_MILES = 50;
export const MAX_NEAR_RADIUS_MILES = 300;

export const ListSubjectsQuery = z
  .object({
    q: z.string().min(1).optional(),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    status: z.preprocess(repeatable, z.array(z.string().min(1)).min(1)).optional(),
    research_status: SubjectResearchStatus.optional(),
    sort: SubjectListSort.default('updated'),
    level: SubjectListLevel.default('tracked'),
    /**
     * **Only the people one import session put on record** (import note §2.6) —
     * what the link at the end of a commit (`/people?import=<id>`) asks for, and
     * what the line above the table then names.
     *
     * It matches a person the session **created or attached to**, following
     * entity redirects, so a person merged since the import is still the person
     * that import produced. A session of this org that wrote nothing, or an id
     * from another org, selects nothing rather than erroring: the filter is a
     * question about people, and the honest answer to "who did that import
     * write" is sometimes nobody.
     */
    import_id: Uuid.optional(),
    /**
     * **Only the people on this protection account** (security fold,
     * 2026-09-22) — the People list's account chip, and the roster route the
     * security vertical used to have of its own.
     *
     * Accepted in a security org and refused in every other with a 400: a club
     * has no accounts, and silently ignoring the filter would show a caller the
     * whole org believing it had narrowed.
     */
    account_id: Uuid.optional(),
    /**
     * **Only the people who live in or around this place** — a city, a region
     * or a country, as a person would write it ("Nashville, TN", "Lisbon",
     * "Texas"). Matched against the city list; a name that is ambiguous or not
     * on it is refused with what it could mean (people-near-place spec). With
     * it the page is ordered nearest first, and `sort` does not apply.
     *
     * Refused in a security org: a list of where principals live is the
     * output the exposure brief is written to avoid.
     */
    near: z.string().trim().min(1).max(200).optional(),
    /** How far around a city `near` reaches, in miles. Only with `near`. */
    radius_miles: z.coerce.number().int().min(1).max(MAX_NEAR_RADIUS_MILES).optional(),
  })
  .strict()
  .refine((query) => query.radius_miles === undefined || query.near !== undefined, {
    path: ['radius_miles'],
    message: 'radius_miles needs near',
  });
export type ListSubjectsQuery = z.infer<typeof ListSubjectsQuery>;
export type ListSubjectsQueryInput = z.input<typeof ListSubjectsQuery>;

/**
 * One row of the People table; `latest_run` backs the "Latest" column.
 *
 * Two fields exist for the wrong-person unwind, which leaves rows the state
 * alone cannot phrase:
 *
 * - `latest_run.error_code` — the failure's code, `IDENTITY_WITHDRAWN` for a run
 *   the unwind stopped. Null for a failure that carried no code, and for every
 *   non-failed run.
 * - `identity_established` — whether the subject has an anchor-set version that
 *   is still bound. False after an unwind, and the reason a *complete* research
 *   run must not read as "Dossier ready": the dossier it produced was
 *   superseded with it.
 *
 * **A known person is a row too** (import note §1.3), and the nullability says
 * exactly which of these fields is a statement about them and which is not.
 * `entity_id` is the one field every row carries and the one to key a row on:
 * `id` is the *subject record's*, which a known person does not have. A known
 * row is `id: null`, `status: null` (no status row and never one), `latest_run:
 * null` (no run is ever created for a non-subject) and `identity_established:
 * false` (an anchor set is a subject's). `vertical`, `name` and `updated_at`
 * mean the same thing at both levels.
 */
/**
 * **Where a person on a `near` page lives**: their residence nearest the place
 * asked about. `stated` is the record's own words; `city`, `region` and
 * `country` are the city list's names for what it matched. A residence placed
 * only at region level ("Texas") has no `city` and no `distance_miles`.
 */
export const NEAR_BASES = ['primary_residence', 'second_home', 'residence'] as const;
/** What each basis is called where a person reads it: the connector, and any list that shows one. */
export const NEAR_BASIS_LABELS: Record<(typeof NEAR_BASES)[number], string> = {
  primary_residence: 'Primary residence',
  second_home: 'Second home',
  residence: 'Residence',
};
export const SubjectListLocation = z.object({
  stated: z.string(),
  basis: z.enum(NEAR_BASES),
  city: z.string().nullable(),
  region: z.string().nullable(),
  country: z.string().nullable(),
  /** Whole miles from the city asked about; null for a region-level match or a region asked about. */
  distance_miles: z.number().int().nonnegative().nullable(),
  /** When a source last confirmed the residence, or null when none dated it. */
  as_of: z.string().nullable(),
  /** Their other residences that also qualify, counted rather than listed. */
  also_nearby: z.number().int().nonnegative(),
});
export type SubjectListLocation = z.infer<typeof SubjectListLocation>;

export const SubjectListEntry = z.object({
  /**
   * The subject record's id — which is the person entity's, the two being one
   * row's worth of the same identity. **Null for a known person**, who has no
   * subject record; read `entity_id` instead.
   */
  id: Uuid.nullable(),
  /** The person entity. Present at both levels, and the row's stable key. */
  entity_id: Uuid,
  name: z.string(),
  /** The org's vertical — which vocabulary `status` is drawn from (§7.9a). */
  vertical: z.string(),
  /** Tracked (a subject) or known (a person the org holds but does not track). */
  level: SubjectLevel,
  /** The vertical's word for a subject; null for a known person. */
  status: SubjectStatus.nullable(),
  latest_run: z
    .object({
      kind: RunKind,
      state: RunState,
      error_code: z.string().nullable(),
      completed_at: Timestamp.nullable(),
    })
    .nullable(),
  identity_established: z.boolean(),
  /**
   * **The protection account this record belongs to**, and `null` in every
   * vertical that has none (security fold, 2026-09-22).
   *
   * Required and `.nullable()` rather than `.optional()`, which is this
   * package's rule for a field that can be "none": an absent key and a key
   * saying "no account" are two different facts, and a client that has to tell
   * a build without the field from a record without an account is holding a
   * union nobody meant to create.
   */
  account: RecordAccount.nullable(),
  updated_at: Timestamp,
  /** Where they live, as far as the `near` filter is concerned; null without `near`. */
  location: SubjectListLocation.nullable(),
});
export type SubjectListEntry = z.infer<typeof SubjectListEntry>;

export const SubjectListResponse = z.object({
  entries: z.array(SubjectListEntry),
  /** Null when the page is the last one. */
  cursor: z.string().nullable(),
  /**
   * With `near`: how many people the same filters reach who have no home the
   * list could place — no residence on record, or one it could not match. The
   * answer's coverage line. Null without `near`.
   */
  without_location: z.number().int().nonnegative().nullable(),
});
export type SubjectListResponse = z.infer<typeof SubjectListResponse>;

/* ————————————————————————— where a person came from ————————————————————————— */

/**
 * **The import session this person arrived on** (import note §2.5) — the record
 * subline's "imported 6 Sep from Contacts.xlsx", linking back to `/imports/:id`.
 *
 * Set for a person a session **created** and for one it **attached to**, because
 * both are answers to "why is this on the record": the first says the person is
 * here because of that file, the second says part of what is on them is. A person
 * nobody imported carries `null`, which is almost everybody.
 *
 * The **earliest** session wins where a person is on more than one file. The
 * subline is one line and the question it answers is where the record came from,
 * not every file that has since touched it; the session page lists its own
 * people, which is the read that goes the other way.
 *
 * It follows entity redirects, so a person merged since their import still names
 * the file they arrived on.
 *
 * Declared here rather than in `import.ts` because `import.ts` already reads this
 * module (`NameConflictCandidate`), and a record block is a fact about a record.
 */
export const SubjectImportRef = z
  .object({
    id: Uuid,
    /** The one line the office wrote about what the file is. */
    description: z.string(),
    filename: z.string(),
    /** When the session was created — the date the subline prints. */
    at: Timestamp,
  })
  .strict();
export type SubjectImportRef = z.infer<typeof SubjectImportRef>;

/* ————————————————————————— the record composition ————————————————————————— */

/**
 * The identity block. Null until any anchor-set version exists. `anchors` are
 * the display strings the record line reads out ("company, city, Scripps
 * board") — shown and confirmed, never form-filled.
 */
export const IdentityBlock = z.object({
  anchor_set_version: z.number().int().positive(),
  state: z.enum(['provisional', 'confirmed']),
  anchors: z.array(z.string()),
  /** When this version was created — the identity line's "Identity provisional · <date>". */
  created_at: Timestamp,
  /** When it was upgraded to confirmed; null while provisional. */
  confirmed_at: Timestamp.nullable(),
});
export type IdentityBlock = z.infer<typeof IdentityBlock>;

/* ————————————————————————— wrong person, on the identity line ————————————————————————— */

/**
 * **Proposed addition — not yet blessed into server-design §3.1.**
 *
 * `POST /orgs/:orgId/subjects/:id/identity/wrong-person` — the office's recourse
 * when the identity behind a record is the wrong human being.
 *
 * It exists because identity is now **locked or asked**: a lock writes a
 * confirmed identity with no question attached, so there is no pending row to
 * answer "wrong person" on. The recourse moves to the identity line itself,
 * where it is available for as long as the identity stands rather than only
 * while a confirmation is outstanding.
 *
 * The body is optional and carries at most `{confirm: true}` — the dialog has
 * already asked, and the server does not require the flag; it is accepted so a
 * caller can be explicit. The unwind is **total** (ui-design §4.5): every
 * standing bind comes undone, OSINT findings are discarded, run-produced
 * analyses are superseded and a run still under way is withdrawn. The correction
 * it logs carries `origin: 'identity_line'`.
 *
 * 409 `IDENTITY_NOT_ESTABLISHED` when there is no bound identity to reject.
 */
export const WrongPersonBody = z
  .object({ confirm: z.literal(true).optional() })
  .strict()
  // An absent body is a valid one: the confirmation already happened in the
  // dialog, and `{confirm: true}` is accepted so a caller can be explicit.
  .default({});
export type WrongPersonBody = z.infer<typeof WrongPersonBody>;

/** What the unwind took down — ids, so a caller can say what happened without a second read. */
export const WrongPersonResponse = z.object({
  /** The recorded `correction` decision the whole fan-out hangs off. */
  correction_decision_id: Uuid,
  removed_lineage_ids: z.array(Uuid),
  superseded_analysis_ids: z.array(Uuid),
  withdrawn_run_ids: z.array(Uuid),
});
export type WrongPersonResponse = z.infer<typeof WrongPersonResponse>;

/* ————————————————————————— delete the person ————————————————————————— */

/**
 * **Proposed addition — not yet blessed into server-design §3.1.**
 *
 * `DELETE /orgs/:orgId/subjects/:id` — the real tombstone-and-shred, per §8's
 * deletion seams. No body, ordinary office auth, 404-not-403 like its siblings.
 *
 * What it does, in one transaction: server-fails any run still under way, writes
 * the terminal `tombstone` decision and stamps `entity.tombstoned_at` (the
 * entity ROW survives — it is the replay barrier and the FK anchor), then runs
 * the shred as a query over the subject-keyed rows. Two carve-outs are the
 * committed semantics rather than convenience: **pair facts skeletonize**
 * (lineage + type + counterparty kept, every version's payload nulled) so the
 * other person's record keeps its own side of the edge, and **org-principal
 * facts are untouched** — shredding Voss must not delete Meridian's
 * recapitalization (§7.2c). Evidence objects are refcounted by evidence rows and
 * deleted from the store only when the last row referencing that sha is gone.
 *
 * A repeat DELETE 404s: a tombstoned subject reads as absent everywhere.
 */
export const DeleteSubjectResponse = z.object({
  deleted: z.literal(true),
  /** The terminal `tombstone` decision — the barrier the shred hangs off. */
  tombstone_decision_id: Uuid,
  /** What went, counted at the point it went. */
  removed: z.object({
    claims: z.number().int(),
    facts: z.number().int(),
    /** Pair facts kept as skeletons for the surviving counterparty. */
    skeletonized_facts: z.number().int(),
    analyses: z.number().int(),
    evidence_rows: z.number().int(),
    /** Stored objects whose last referencing row was this subject's. */
    evidence_objects: z.number().int(),
    runs: z.number().int(),
  }),
});
export type DeleteSubjectResponse = z.infer<typeof DeleteSubjectResponse>;

/**
 * A prose input whose extraction failed — the record's retry banner reads this,
 * one entry per failed note. The note's own line in `notes` carries the same
 * state, so the two never disagree about a given input.
 */
export const ExtractionFailureEntry = z.object({
  input_id: Uuid,
  kind: ProseInputKind,
  /** The first stretch of the note's prose, so the banner can name which note failed. */
  excerpt: z.string(),
  attempt: z.number().int().nonnegative(),
  started_at: Timestamp.nullable(),
  finished_at: Timestamp.nullable(),
  /** The reader's sentence for why; null for rows that failed before the reason was recorded. */
  reason: z.string().nullable(),
  /**
   * **The paste this row is a piece of**, or null for a note stored whole
   * (split-giant-note note, 2026-09-14). The banner needs both of these to say
   * *Piece 3 of a pasted note could not be read*: on a paste, an excerpt alone
   * names a fragment the office never typed as a unit, and a reader looking at
   * eight rows from one paste has no way to tell which of them stopped.
   */
  split_group_id: Uuid.nullable(),
  /** Where this piece sits in its paste, 1-based; null with `split_group_id`. */
  piece_seq: z.number().int().positive().nullable(),
  created_at: Timestamp,
});
export type ExtractionFailureEntry = z.infer<typeof ExtractionFailureEntry>;

export const ProfileLinkPlatform = z.enum(PROFILE_LINK_PLATFORMS);
export type ProfileLinkPlatform = z.infer<typeof ProfileLinkPlatform>;

/** The note an account was read out of: when, and the sentence that names it. */
export const ProfileLinkNote = z.object({
  input_id: Uuid,
  created_at: Timestamp,
  /** Null when the note does not write the account the way the reader stored it. */
  sentence: z.string().nullable(),
});
export type ProfileLinkNote = z.infer<typeof ProfileLinkNote>;

export const ProfileLink = z.object({
  anchor_id: Uuid,
  platform: ProfileLinkPlatform,
  /** "@dhart_sf", a wallet address, or an on-chain identity's name. */
  handle: z.string(),
  /**
   * A public page for the account, or null: an on-chain identity links no
   * provider page (spec §3), and a wallet links an explorer only when its chain
   * is known.
   */
  url: z.string().nullable(),
  /** A wallet's chain as the source named it ("dogecoin"); null when unnamed and on every non-wallet link. */
  chain: z.string().nullable(),
  /** Null for an account no note named, and for anchors older than 2026-09-26. */
  note: ProfileLinkNote.nullable(),
});
export type ProfileLink = z.infer<typeof ProfileLink>;

/**
 * **A person's X and Bluesky accounts and crypto wallets, for the office to
 * manage** (docs/notes/2026-09-26-profile-links-and-principal-social-spec.md,
 * docs/notes/2026-10-01-onchain-intel-arkham-spec.md).
 *
 * `suggested` are accounts a note named that nobody has ruled on; research does
 * not read them. `confirmed` are the ones the office said are this person's;
 * research reads their posts. Neither establishes who the person is.
 */
export const ProfileLinks = z.object({
  suggested: z.array(ProfileLink),
  confirmed: z.array(ProfileLink),
});
export type ProfileLinks = z.infer<typeof ProfileLinks>;

/**
 * `POST /subjects/:id/profile-links/:anchorId` — owner-only.
 *
 * `confirm` and `set_aside` answer a suggestion; `remove` takes a confirmed
 * account back. Set aside and removed accounts are not suggested again.
 */
export const ProfileLinkAction = z.enum(PROFILE_LINK_ACTIONS);
export type ProfileLinkAction = z.infer<typeof ProfileLinkAction>;

export const ProfileLinkBody = z.object({ action: ProfileLinkAction }).strict();
export type ProfileLinkBody = z.infer<typeof ProfileLinkBody>;

export const ProfileLinkResponse = ProfileLinks;
export type ProfileLinkResponse = z.infer<typeof ProfileLinkResponse>;

/**
 * `GET /subjects/:id/record` (and the `POST /subjects` response) — the composed
 * record page, per server-design.md §3.1.
 *
 * Composition rules the payload already reflects: negated facts are listed
 * (auditable), held claims are listed as pending, and pair / org-context facts
 * are unioned in from the counterparty side and rendered in the reader's
 * direction (§7.2c). Every read of this route evaluates the lazy reaper first,
 * so a dead run shows `failed` on that page load.
 */
export const SubjectRecord = z.object({
  subject: z.object({
    id: Uuid,
    name: z.string(),
    /**
     * The org's vertical — which vocabulary `status` is drawn from (§7.9a). The
     * dashboard keys its status labels by this rather than assuming club.
     */
    vertical: z.string(),
    status: SubjectStatus,
    /**
     * When the status was last switched, and `null` for a subject that still
     * carries the word it was entered under. Read off the newest
     * `status_change` decision rather than a column, so the log is the history.
     *
     * It is not "member since": what it dates is the office marking the change,
     * which may be long after the club made it.
     */
    status_changed_at: Timestamp.nullable(),
    /**
     * The analysis kinds the **current** status derives — what the next run
     * would write, from the org's vertical (§5.2). A reading already on the
     * record whose kind is not in here was written under an earlier status and
     * is kept, not revisited; the brief says so beside it rather than hiding it.
     */
    analysis_kinds: z.array(z.string()),
    /** Routed first-party operational data with per-field attribution. */
    record_fields: JsonObject,
    /**
     * **The user of this system this person *is*** (import note §1.14) — the
     * `org_membership` of the **reader's own org** whose `person_entity_id` is
     * this subject, resolved to the account behind it. Null for the overwhelming
     * majority of records, which are people the org researches rather than
     * people who log in.
     *
     * It is org-scoped by construction and not by filter: the read is already
     * inside an org, and the same person entity cannot be a member of another
     * org's roster because entities are per-org (data-model §2.3).
     *
     * The record head reads it as **"This is you"** when the id is the caller's
     * and "This is <name>" otherwise — which is why the account's `email` rides
     * along: `app_user.name` is null until a first sign-in, and a subline that
     * said "This is " would be worse than one that named an address.
     */
    linked_user: LinkedUser.nullable(),
    /** The file this person arrived on — see {@link SubjectImportRef}. */
    import: SubjectImportRef.nullable(),
    /** The protection account this person is on; null in every vertical that has none. */
    account: RecordAccount.nullable(),
    created_at: Timestamp,
    updated_at: Timestamp,
  }),
  identity: IdentityBlock.nullable(),
  /**
   * **The confirmed identifiers the standing version does not name** — the
   * display strings, as the identity block's own `anchors` reads them.
   *
   * Never an overlap with `identity.anchors` and never a second rendering of it:
   * the two together are the person's whole confirmed set, split by whether the
   * established identity accounts for the row.
   *
   * It exists because of the 2026-09-15 ruling that an **import does not
   * establish identity** (import note §1.5, §4.6), and it answers in both of
   * that ruling's states. With **no identity** — every imported person until a
   * run confirms them — it is all of their confirmed identifiers, and the record
   * says "3 identifiers on the record. Identity is not yet confirmed." rather
   * than leaving the true half out. With **an identity**, it is whatever is
   * outside the set: a version's membership is the confirmed set *as it stood
   * when the version was minted*, and an import attaching to somebody already
   * identified writes their member number afterwards. Empty whenever there is
   * nothing outside it, which is the ordinary case for an identified person.
   */
  anchors_on_record: z.array(z.string()),
  /**
   * The person's X and Bluesky accounts, for an owner to manage; null for every
   * other reader, who is not shown them (ruling 2026-09-26).
   */
  profile_links: ProfileLinks.nullable(),
  facts: z.array(Fact),
  /**
   * Claims held at ingest — no lineage exists for them yet (§7.2 step 7). They
   * render as "pending review" beside the facts and never silently sink.
   */
  held_claims: z.array(HeldClaim),
  analyses: z.array(Analysis),
  pending_decisions: z.array(DecisionSummary),
  runs: z.array(RunSummary),
  notes: z.array(NoteEntry),
  /**
   * When a note research could read was last edited or removed, or null. A
   * brief written before it read words that are no longer the record's, which
   * is what the Research tab's update line compares against; new notes say so
   * by their own `created_at`.
   */
  notes_changed_at: Timestamp.nullable(),
  interactions: z.array(InteractionEntry),
  extraction_failures: z.array(ExtractionFailureEntry),
});
export type SubjectRecord = z.infer<typeof SubjectRecord>;

/**
 * `POST /subjects/:id/status` — the office switching what a person is to the
 * org. The response is the record's `subject` block, so the caller re-reads the
 * status, the switch date and the derived kinds from one answer.
 *
 * **Any word of the org's vertical to any other**, with no rule about the order
 * (club-statuses note §1.3): the vertical vets only that the word is one of
 * its own, and a wrong click is undone by the next click. A word from another
 * vertical is a 400 `VALIDATION_FAILED` naming this org's type. Posting the word
 * the subject already carries is a no-op that records nothing.
 *
 * Nothing else moves: facts stay, every current analysis stays current and stays
 * rendered, no run starts, and a run already under way keeps the kinds and depth
 * it was created with.
 */
export const SetSubjectStatusBody = z.object({ status: SubjectStatus }).strict();
export type SetSubjectStatusBody = z.infer<typeof SetSubjectStatusBody>;

/** The switch answers the record's `subject` block — no second shape. */
export const SetSubjectStatusResponse = SubjectRecord.shape.subject;
export type SetSubjectStatusResponse = z.infer<typeof SetSubjectStatusResponse>;

/** `POST /subjects` returns the seeded record — same schema, no second shape. */
export const CreateSubjectResponse = SubjectRecord;
export type CreateSubjectResponse = z.infer<typeof CreateSubjectResponse>;
